import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import { object, string, InferType } from "yup";
import { useForm } from "react-hook-form";
import { t } from "i18next";
import { tv } from "tailwind-variants";
import { toast } from "react-toastify";

import { usePostSendDeveloperEmail } from "@/requests/hooks/comply";
import useIsMobile from "@/utils/useIsMobile";

import { Modal } from "@/components/Modal";
import { H4 } from "@/components/Typography";
import { Input } from "@/components/Input";
import { Button } from "@/components/Button";
import { Form } from "@/components/Form";

const styles = tv({
  slots: {
    base: "flex flex-col items-center justify-center p-20",
    section:
      "mt-15 flex w-full flex-row items-center justify-evenly sm:flex-col sm:items-center",
    ctaWrapper: "flex flex-col justify-between gap-20 sm:flex-row sm:gap-40",
  },
});

const formSchema = object({
  email: string()
    .email(
      t(
        "comply.privacyCenter.websiteInstallation.emailDeveloper.form.errors.emailInvalid",
      ),
    )
    .required(
      t(
        "comply.privacyCenter.websiteInstallation.emailDeveloper.form.errors.emailRequired",
      ),
    ),
});

type IEmailDeveloperForm = InferType<typeof formSchema>;

interface IEmailDeveloperModalProps {
  isOpen: boolean;
  closeModal: () => void;
}

const EmailDeveloperModal = ({
  isOpen,
  closeModal,
}: IEmailDeveloperModalProps) => {
  const { t } = useTranslation();
  const { base, section, ctaWrapper } = styles();
  const isMobile = useIsMobile();

  const {
    mutate: postSendDeveloperEmail,
    isPending: isPostSendDeveloperEmailLoading,
    isSuccess: isPostSendDeveloperEmailSuccess,
  } = usePostSendDeveloperEmail();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<IEmailDeveloperForm>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });

  const handleOnCancel = () => {
    reset();
    closeModal();
  };

  const onSubmit = ({ email }: IEmailDeveloperForm) => {
    postSendDeveloperEmail(email);
  };

  useEffect(() => {
    if (isPostSendDeveloperEmailSuccess) {
      reset();
      toast.success(
        t(
          "comply.privacyCenter.websiteInstallation.emailDeveloper.form.successMessage",
        ),
      );
      closeModal();
    }
  }, [isPostSendDeveloperEmailSuccess]);

  return (
    <Modal isOpen={isOpen} toggleIsOpen={closeModal} width="md" rounded="sm">
      <div className={base()}>
        <H4>
          {t(
            "comply.privacyCenter.websiteInstallation.emailDeveloper.description",
          )}
        </H4>
        <div className={section()}>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Input
              label={t(
                "comply.privacyCenter.websiteInstallation.emailDeveloper.form.input.label",
              )}
              placeholder={t(
                "comply.privacyCenter.websiteInstallation.emailDeveloper.form.input.placeholder",
              )}
              type="email"
              {...register("email")}
              helperText={errors?.email?.message}
              variant={errors?.email && "error"}
              testId="email-input"
              aria-invalid={!!errors?.email}
            />

            <div className={ctaWrapper()}>
              <Button
                type="button"
                variant="secondary-outline"
                width={isMobile ? "full" : "md"}
                onClick={handleOnCancel}>
                {t(
                  "comply.privacyCenter.websiteInstallation.emailDeveloper.form.button.cancel",
                )}
              </Button>
              <Button
                trackingId="support.send-to-developer"
                variant="primary"
                type="submit"
                width={isMobile ? "full" : "md"}
                loading={isPostSendDeveloperEmailLoading}
                disabled={!isValid}
                data-testid="login-cta">
                {t(
                  "comply.privacyCenter.websiteInstallation.emailDeveloper.form.button.cta",
                )}
              </Button>
            </div>
          </Form>
        </div>
      </div>
    </Modal>
  );
};

export default EmailDeveloperModal;
